import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import Reveal from "@/components/Reveal";
import { Radar, Cpu, Waves, Sprout } from "lucide-react";

function Counter({ to, decimals = 0, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(to * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return (
    <span ref={ref} className="font-mono">
      {val.toLocaleString("en-IN", { maximumFractionDigits: decimals, minimumFractionDigits: decimals })}
      {suffix}
    </span>
  );
}

const CAPS = [
  { icon: Radar, title: "Multi-sensor fusion", desc: "Optical, SAR, thermal and AIS streams unified into one temporal ground-truth layer." },
  { icon: Cpu, title: "AI change detection", desc: "Models flag anomalies. a vanished vessel wake, a stressed crop canopy. within minutes." },
  { icon: Waves, title: "Maritime awareness", desc: "Persistent watch over India's EEZ: dark vessels, spills, and coastal change." },
  { icon: Sprout, title: "Agri intelligence", desc: "Field-level crop health, soil moisture and yield forecasting at national scale." },
];

const STATS = [
  { to: 7517, suffix: " km", label: "Coastline under continuous watch", decimals: 0 },
  { to: 4.2, suffix: "M ha", label: "Farmland monitored across India", decimals: 1 },
  { to: 12480, suffix: "", label: "Vessels tracked every 24 hours", decimals: 0 },
  { to: 30, suffix: " min", label: "Median revisit for priority zones", decimals: 0 },
];

export default function Platform() {
  return (
    <section id="platform" className="relative bg-arjuna-ink py-24 sm:py-32" data-testid="platform">
      <div className="container-x">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline">01. The Platform</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">
                A single lens over India&apos;s water and land.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
                Arjuna Space turns a constant stream of earth-observation data into a
                living operational picture. One platform, two frontiers. the ocean and
                the field. engineered for analysts, agencies and agri-enterprises.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 sm:grid-cols-2 lg:col-span-7">
            {CAPS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="group h-full bg-arjuna-surface/50 p-8 transition-colors duration-300 hover:bg-arjuna-surface">
                  <c.icon className="h-7 w-7 text-arjuna-teal transition-transform duration-300 group-hover:scale-110" />
                  <h3 className="mt-6 font-heading text-lg text-white">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* stat band */}
        <div className="mt-20 grid grid-cols-2 gap-8 border-t border-white/10 pt-12 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div>
                <div className="text-4xl font-semibold text-arjuna-teal sm:text-5xl">
                  <Counter to={s.to} suffix={s.suffix} decimals={s.decimals} />
                </div>
                <p className="mt-3 text-sm text-slate-400">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
